import { todayIn } from '../utils/date-utils';
/**
 * Habit adherence, read from the records that actually exist.
 *
 * A habit is a recurring task, so completing one does NOT set
 * `status: 'completed'` — `useTasks.completeTask` resets it to `'todo'` for
 * the next occurrence and writes a `HabitInstance` instead. Every consumer
 * that asked "which habits are completed?" by filtering task status was
 * therefore filtering an empty set, and rendering a confident zero: the
 * weekly review showed 0% adherence for every habit, the journal's
 * "completed today" never listed a habit, badge progress for habits sat at
 * 0, and the habit component of goal progress contributed nothing.
 *
 * These helpers exist so the answer is computed once, from
 * `HabitInstance.habit_id` / `.date`, and so a shape test can pin those two
 * field names down.
 */
/** An instance's date is a plain YYYY-MM-DD local date string. */
function inRange(date, start, end) {
    if (start && date < start)
        return false;
    if (end && date > end)
        return false;
    return true;
}
/** Instances for one habit, optionally within an inclusive date range. */
export function habitInstancesFor(instances, habitId, start, end) {
    return instances.filter((i) => i.habit_id === habitId && inRange(i.date, start, end));
}
/** Was this habit completed on this exact date? */
export function habitCompletedOn(instances, habitId, date) {
    return instances.some((i) => i.habit_id === habitId && i.date === date);
}
/** Every habit completed on one date, as habit ids. */
export function habitsCompletedOn(instances, date) {
    return [...new Set(instances.filter((i) => i.date === date).map((i) => i.habit_id))];
}
/**
 * habit id → number of completions in the range. This is the shape
 * `computeGoalProgress` and `ReviewService` both take.
 */
export function habitCompletionCounts(instances, start, end) {
    const counts = {};
    for (const instance of instances) {
        if (!inRange(instance.date, start, end))
            continue;
        counts[instance.habit_id] = (counts[instance.habit_id] ?? 0) + 1;
    }
    return counts;
}
/** Total completions across all habits in the range. */
export function totalHabitCompletions(instances, start, end) {
    return instances.filter((i) => inRange(i.date, start, end)).length;
}
/**
 * The local day a task was completed, in the user's zone, or null.
 *
 * `completed_at` is a UTC instant. Reading its date part (`slice(0, 10)`)
 * files an LA user's 7pm completion under tomorrow; reading it with the
 * machine's local getters is right on the device and wrong on the server,
 * which runs in UTC. This is the one reading.
 */
export function completedOnKey(task, timezone) {
    if (!task.completed_at)
        return null;
    const at = new Date(task.completed_at);
    if (Number.isNaN(at.getTime()))
        return null;
    return todayIn(timezone, at);
}
/**
 * The distinct dates on which *anything* was completed — habit instances plus
 * task completions. Used for streaks, where a day spent only on habits still
 * counts as a day worked.
 */
export function activeDates(tasks, instances, 
/** The user's zone. On the device it may be omitted; on the server it must not be. */
timezone) {
    const dates = new Set();
    for (const task of tasks) {
        if (task.is_habit || task.status !== 'completed')
            continue;
        const day = completedOnKey(task, timezone);
        if (day)
            dates.add(day);
    }
    for (const instance of instances) {
        if (instance.date)
            dates.add(instance.date);
    }
    return [...dates].sort();
}
//# sourceMappingURL=habit-stats.js.map